import React, { useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import {
  MemoTemplateBuilder,
  SectionNodeErrors,
  SectionNodeDefaultValues,
  ChecklistItemNodeErrors,
  ChecklistItemNodeDefaultValues,
} from 'views/template/builder';
import {
  ChecklistItemSource,
  ChecklistSectionSource,
} from 'shared/domain/checklistTree';
import { ChecklistBuilderProps } from './types';
import {
  FormChecklistAnswer,
  FormChecklistItem,
  FormSection,
  ChecklistItemValidationFocus,
} from 'views/template/wizard/form/types';
import { CHECKLIST_BUILDER_CONTAINER_SX } from './styles';
import { AnswerConfig } from 'views/template/builder/components/checklistItemNode/answerConfig';
import { hasAnswersTabErrors } from 'views/template/wizard/form/validation';

function formSectionToBuilderSection(
  section: FormSection
): ChecklistSectionSource {
  return {
    _id: section._id || section.uniqueKeyId,
    title: section.title,
    description: section.description,
    order: section.order,
    parentSectionId: section.parentSectionId,
    deleted: false,
  };
}

function formItemToBuilderItem(item: FormChecklistItem): ChecklistItemSource {
  return {
    _id: item._id || item.uniqueKeyId,
    summary: item.summary,
    norm: {
      description: item.description,
    },
    index: 0,
    order: item.order,
    parentSectionId: item.parentSectionId,
    answers: [],
    deleted: false,
  };
}

function ChecklistBuilder({
  sections,
  items,
  defaultSections,
  defaultItems,
  onSectionTitleChange,
  onSectionDescriptionChange,
  onSectionGraphicUploaderInit,
  onAddSection,
  onDeleteSection,
  onItemSummaryChange,
  onItemDescriptionChange,
  onItemGraphicUploaderInit,
  onAddItem,
  onDeleteItem,
  onReorderSection,
  onReorderItem,
  sectionGraphicUploaders,
  itemGraphicUploaders,
  onAddAnswer,
  onUpdateAnswer,
  onDeleteAnswer,
  checklistValidationFocus,
}: ChecklistBuilderProps): React.ReactElement {
  const sectionsDto = useMemo(
    () => sections.map(formSectionToBuilderSection),
    [sections]
  );

  const itemsDto = useMemo(() => items.map(formItemToBuilderItem), [items]);

  const sectionErrors = useMemo(() => {
    const errors: Record<string, SectionNodeErrors> = {};
    sections.forEach((section) => {
      const id = section._id || section.uniqueKeyId;
      if (section.errors.title || section.errors.description) {
        errors[id] = {
          title: section.errors.title,
          description: section.errors.description,
        };
      }
    });
    return errors;
  }, [sections]);

  const sectionDefaultValues = useMemo(() => {
    const values: Record<string, SectionNodeDefaultValues> = {};
    const defaults = defaultSections || sections;
    defaults.forEach((section) => {
      const id = section._id || section.uniqueKeyId;
      values[id] = {
        title: section.title,
        description: section.description,
      };
    });
    return values;
  }, [defaultSections, sections]);

  const itemErrors = useMemo(() => {
    const errors: Record<string, ChecklistItemNodeErrors> = {};
    items.forEach((item) => {
      const id = item._id || item.uniqueKeyId;
      if (
        item.errors.summary ||
        item.errors.description ||
        hasAnswersTabErrors(item.errors.answers)
      ) {
        errors[id] = {
          summary: item.errors.summary,
          description: item.errors.description,
          answers: item.errors.answers,
        };
      }
    });
    return errors;
  }, [items]);

  const itemDefaultValues = useMemo(() => {
    const values: Record<string, ChecklistItemNodeDefaultValues> = {};
    const defaults = defaultItems || items;
    defaults.forEach((item) => {
      const id = item._id || item.uniqueKeyId;
      values[id] = {
        summary: item.summary,
        description: item.description,
      };
    });
    return values;
  }, [defaultItems, items]);

  const itemAnswers = useMemo(() => {
    const answers: Record<string, AnswerConfig[]> = {};
    items.forEach((item) => {
      const id = item._id || item.uniqueKeyId;
      if (item.answers && item.answers.length > 0) {
        answers[id] = item.answers;
      }
    });
    return answers;
  }, [items]);

  const handleSectionTitleChange = useCallback(
    (sectionId: string, title: string) => {
      onSectionTitleChange(sectionId, title);
    },
    [onSectionTitleChange]
  );

  const handleSectionDescriptionChange = useCallback(
    (sectionId: string, description: string) => {
      onSectionDescriptionChange(sectionId, description);
    },
    [onSectionDescriptionChange]
  );

  const handleItemSummaryChange = useCallback(
    (itemId: string, summary: string) => {
      onItemSummaryChange(itemId, summary);
    },
    [onItemSummaryChange]
  );

  const handleItemDescriptionChange = useCallback(
    (itemId: string, description: string) => {
      onItemDescriptionChange(itemId, description);
    },
    [onItemDescriptionChange]
  );

  return (
    <Box sx={CHECKLIST_BUILDER_CONTAINER_SX}>
      <MemoTemplateBuilder
        sections={sectionsDto}
        items={itemsDto}
        onReorderSection={onReorderSection}
        onReorderItem={onReorderItem}
        onSectionTitleChange={handleSectionTitleChange}
        onSectionDescriptionChange={handleSectionDescriptionChange}
        onSectionGraphicUploaderInit={onSectionGraphicUploaderInit}
        onDeleteSection={onDeleteSection}
        onItemSummaryChange={handleItemSummaryChange}
        onItemDescriptionChange={handleItemDescriptionChange}
        onItemGraphicUploaderInit={onItemGraphicUploaderInit}
        onDeleteItem={onDeleteItem}
        onAddSection={onAddSection}
        onAddItem={onAddItem}
        sectionErrors={sectionErrors}
        sectionDefaultValues={sectionDefaultValues}
        sectionGraphicUploaders={sectionGraphicUploaders}
        itemErrors={itemErrors}
        itemDefaultValues={itemDefaultValues}
        itemGraphicUploaders={itemGraphicUploaders}
        itemAnswers={itemAnswers}
        onAddAnswer={onAddAnswer}
        onUpdateAnswer={onUpdateAnswer}
        onDeleteAnswer={onDeleteAnswer}
        checklistValidationFocus={checklistValidationFocus}
      />
    </Box>
  );
}

export const MemoChecklistBuilder = React.memo(ChecklistBuilder);
export { ChecklistBuilder };
export type { ChecklistBuilderProps } from './types';
